// Phase 9.1.B.2 — Forgot password (start flow).
//
// Calls POST /portal/auth/forgot-password. Backend always returns 202
// regardless of whether the email is a known portal user (no enumeration),
// so the UI shows a "check your inbox" confirmation unconditionally on
// success — same UX shape for both real users + typo'd emails.
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api("/portal/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
        skipAuth: true,
      });
    } catch {
      // Service is always-202; swallow any transient error to preserve
      // the no-enumeration UX. The user retries via the same form.
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
        <div className="w-full max-w-md text-center">
          <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-md bg-peach-400">
            <span className="text-2xl font-bold text-brown-500">✓</span>
          </div>
          <h1 className="mb-4 font-display text-3xl font-bold tracking-tight">
            Check your email
          </h1>
          <p className="mb-8 text-brown-400">
            If an account exists for{" "}
            <span className="font-semibold text-brown-500">{email}</span>,
            we just sent a reset link. It expires in 1 hour.
          </p>
          <p className="text-sm text-brown-400">
            <Link
              to="/login"
              className="font-semibold text-ink underline-offset-2 hover:underline"
            >
              Back to sign-in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream-50 px-6 py-16">
      <div className="w-full max-w-md">
        <h1 className="mb-2 font-display text-4xl font-bold tracking-tight">
          Reset your password
        </h1>
        <p className="mb-10 text-brown-400">
          Enter your email and we'll send you a reset link.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-brown-500">
              Email
            </span>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              autoComplete="email"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-ink px-4 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send reset link"}
          </button>
        </form>

        <p className="mt-8 text-sm text-brown-400">
          <Link
            to="/login"
            className="font-semibold text-ink underline-offset-2 hover:underline"
          >
            Back to sign-in
          </Link>
        </p>
      </div>
    </div>
  );
}
